/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import React, { ReactNode, useEffect, useState } from 'react';
import i18n, {
  changeLanguage,
  DEFAULT_LANGUAGE,
  isLanguageSupported,
  SupportedLocales,
  TranslationJson,
} from './i18n';
import { LocaleContext, LocaleContextType } from './useLocale';
import { I18nextProvider } from 'react-i18next';

export interface TranslationResources {
  [language: string]: {
    // eslint-disable-next-line
    [namespace: string]: TranslationJson;
  };
}

// Props for the provider
interface LocaleProviderProps {
  children: ReactNode;
  initialLocale?: SupportedLocales;
  namespace: string;
  translationResources?: TranslationResources;
  fallbackLocale?: SupportedLocales;
  onLocaleChange?: (locale: SupportedLocales) => void;
  debug?: boolean;
}

const LocaleProvider: React.FC<LocaleProviderProps> = ({
  children,
  initialLocale = DEFAULT_LANGUAGE,
  fallbackLocale = DEFAULT_LANGUAGE,
  onLocaleChange,
}) => {
  const [currentLanguage, setCurrentLanguage] =
    useState<SupportedLocales>(initialLocale);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize i18n with dynamic configuration
  useEffect(() => {
    const initializeI18n = async () => {
      try {
        setIsLoading(true);

        const languageToUse = initialLocale;

        // Check if the language is supported
        if (isLanguageSupported(languageToUse)) {
          await changeLanguage(languageToUse);
          setCurrentLanguage(languageToUse);
        } else {
          console.warn(
            `Language ${languageToUse} is not supported, falling back to ${fallbackLocale}`,
          );
          await changeLanguage(fallbackLocale);
          setCurrentLanguage(fallbackLocale);
        }
      } catch (error) {
        console.error('Failed to initialize i18n:', error);
        setCurrentLanguage(fallbackLocale);
      } finally {
        setIsLoading(false);
      }
    };

    initializeI18n();
  }, [fallbackLocale, initialLocale]);

  // Handle language changes
  const handleLocaleChange = async (
    language: SupportedLocales,
  ): Promise<boolean> => {
    try {
      setIsLoading(true);
      const success = await changeLanguage(language);
      if (success) {
        setCurrentLanguage(language);
        onLocaleChange?.(language);
      }
      return success;
    } catch (error) {
      console.error('Failed to change language:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: LocaleContextType = {
    currentLanguage,
    changeLanguage: handleLocaleChange,
    isLoading,
  };

  return (
    <LocaleContext.Provider value={contextValue}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </LocaleContext.Provider>
  );
};

export default LocaleProvider;
